import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { api } from '../lib/api';
import { useToast } from './useToast';

export default function AdminCakeList() {
  const [cakes, setCakes] = useState([]);
  const [category, setCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, showToast] = useToast();

  async function load(selected = category) {
    setLoading(true);
    const params = { includeUnavailable: 'true' };
    if (selected && selected !== 'All') params.category = selected;
    const { cakes } = await api.getCakes(params);
    setCakes(cakes);

    // Build category list from returned cakes
    const cats = Array.from(new Set(cakes.map(c => c.category).filter(Boolean))).sort();
    setCategories(['All', ...cats]);

    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  useEffect(() => { load(category); }, [category]);

  // Simple custom dropdown component (inline) to avoid native OS popup and
  // allow theming that matches the admin look.
  function FilterDropdown({ categories, value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef();

    useEffect(() => {
      function onDoc(e) {
        if (!ref.current) return;
        if (!ref.current.contains(e.target)) setOpen(false);
      }
      document.addEventListener('click', onDoc);
      return () => document.removeEventListener('click', onDoc);
    }, []);

    return (
      <div className="admin-filter-dropdown" ref={ref}>
        <button type="button" className="admin-filter-btn" onClick={() => setOpen(o => !o)}>
          {value}
          <span className="admin-filter-caret">▾</span>
        </button>
        {open && (
          <ul className="admin-filter-menu" role="menu">
            {categories.map(cat => (
              <li
                key={cat}
                role="menuitem"
                className={`admin-filter-item ${value === cat ? 'selected' : ''}`}
                onClick={() => { onChange(cat); setOpen(false); }}
              >
                {value === cat ? '✓ ' : ''}{cat}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  async function toggleAvailability(cake) {
    try {
      await api.updateCake(cake.id, { isAvailable: !cake.isAvailable });
      showToast(cake.isAvailable ? 'Marked as sold out' : 'Marked as available');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    }
  }

  async function handleDelete(cake) {
    if (!window.confirm(`Delete "${cake.name}"? This can't be undone.`)) return;
    try {
      await api.deleteCake(cake.id);
      showToast('Cake deleted');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    }
  }

  return (
    <AdminLayout title="Cakes">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--text-mid)', margin: 0 }}>
            {loading ? 'Loading…' : `${cakes.length} cake${cakes.length === 1 ? '' : 's'} total`}
          </p>

          <div className="admin-filter-dropdown">
            <FilterDropdown
              categories={categories}
              value={category}
              onChange={c => setCategory(c)}
            />
          </div>
        </div>
        <Link to="/admin/cakes/new" className="admin-btn admin-btn-primary">+ Add Cake</Link>
      </div>

      {loading ? (
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-mid)' }}>Loading cakes…</p>
      ) : cakes.length === 0 ? (
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-mid)' }}>
          No cakes added yet. Click "Add Cake" to create your first one.
        </p>
      ) : (
        <div className="admin-list">
          {cakes.map(cake => (
            <div className="admin-row" key={cake.id}>
              <img src={cake.image} alt="" className="admin-row-thumb" />
              <div className="admin-row-info">
                <p className="admin-row-name">{cake.name}</p>
                <p className="admin-row-meta">
                  {cake.category} &middot; PKR {cake.price?.toLocaleString()}
                  {!cake.isAvailable && ' · Sold out'}
                  {cake.featured && ' · Featured'}
                </p>
              </div>
              <div className="admin-row-actions">
                <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => toggleAvailability(cake)}>
                  {cake.isAvailable ? 'Mark Sold Out' : 'Mark Available'}
                </button>
                <Link to={`/admin/cakes/${cake.id}`} className="admin-btn admin-btn-outline admin-btn-sm">
                  Edit
                </Link>
                <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => handleDelete(cake)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && <div className={`admin-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.message}</div>}
    </AdminLayout>
  );
}
